import { useState } from "react";
import { api } from "@/api/client";
import { useAsyncData } from "@/hooks/useAsyncData";
import EventCard from "@/components/EventCard";
import ErrorState from "@/components/ErrorState";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Calendar, Loader2 } from "lucide-react";

export default function Events() {
  const { data, loading, error, reload } = useAsyncData(() => api.events.list(), []);
  const events = data ?? [];
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  const categories = ["All", ...new Set(events.map(e => e.category).filter(Boolean))];

  const filtered = events.filter(e => {
    const matchesSearch = !search ||
      e.title?.toLowerCase().includes(search.toLowerCase()) ||
      e.description?.toLowerCase().includes(search.toLowerCase()) ||
      e.location?.toLowerCase().includes(search.toLowerCase()) ||
      e.venue_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.venue_city?.toLowerCase().includes(search.toLowerCase());
    const matchesCat = category === "All" || e.category === category;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center text-white">
          <h1 className="text-4xl font-bold mb-2">All Events</h1>
          <p className="text-indigo-200">Find your next unforgettable experience</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, location..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white rounded-xl"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <Badge
                key={cat}
                onClick={() => setCategory(cat)}
                className={`cursor-pointer px-3 py-1.5 text-sm ${
                  category === cat
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                }`}
                variant="outline"
              >
                {cat}
              </Badge>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : filtered.length > 0 ? (
          <>
            <p className="text-sm text-gray-400 mb-5">{filtered.length} event{filtered.length !== 1 ? "s" : ""} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(event => <EventCard key={event.id} event={event} />)}
            </div>
          </>
        ) : (
          <div className="text-center py-20 text-gray-400">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No events found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
}